/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, MouseEvent, useCallback} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {SearchType} from '@ion/core/src/types/services';
import {AssetDriverIcon} from '../../icons/AssetDriverIcon';
import {AssetTrailerIcon} from '../../icons/AssetTrailerIcon';
import {AssetVehicleIcon} from '../../icons/AssetVehicleIcon';
import {GeofenceTypeIcon} from '../../icons/GeofenceTypeIcon';
import {MapBookmarkIcon} from '../../icons/MapBookmarkIcon';
import {MaterialExitCloseIcon} from '../../icons/MaterialExitCloseIcon';
import {ScDeviceIcon} from '../../icons/ScDeviceIcon';
import Render from '../../render/Render';
import {dlsColor} from '../../theme/theme';
import {IconSize} from '../../types/types';
import {Option} from './AutocompleteSearch';
import {getSearchOverlayOptionName} from './SearchOverlayUtils';
import {renderName} from './SearchUtils';

export interface SearchOverlayRowProps {
  classes: any;
  onRecentSearchRemove?: (option: Option) => void;
  onResultClick: (option: Option) => void;
  option: Option;
  searchValue: string;
}

const getOptionIcon = (type?: SearchType) => {
  const iconMap: Partial<Record<SearchType, React.ComponentType<any>>> = {
    [SearchType.asset]    : AssetVehicleIcon,
    [SearchType.vehicle]  : AssetVehicleIcon,
    [SearchType.geofence] : GeofenceTypeIcon,
    [SearchType.driver]   : AssetDriverIcon,
    [SearchType.place]    : MapBookmarkIcon,
    [SearchType.trailer]  : AssetTrailerIcon,
    [SearchType.scdevice] : ScDeviceIcon,
    [SearchType.fcdevice] : ScDeviceIcon
  };
  return type ? iconMap[type] : undefined;
};

/**
 * Renders one selectable tracking search row.
 *
 * ```typescript
 * import SearchOverlayRow from '@ion/dls/src/components/search/SearchOverlayRow';
 *
 * ...
 *
 * <SearchOverlayRow option={option}
 *   searchValue={searchValue}
 *   classes={classes}
 *   onResultClick={selectResult} />
 * ```
 *
 * @param props Defines the row option and interaction callbacks.
 * @returns A selectable search row with optional recent-search removal.
 */
export const SearchOverlayRow = ({
  classes,
  onRecentSearchRemove,
  onResultClick,
  option,
  searchValue
}: SearchOverlayRowProps) => {
  const TypeIcon = getOptionIcon(option.type);
  const name     = getSearchOverlayOptionName(option);
  const rowIcon  = TypeIcon
    ? <TypeIcon size={IconSize.medium} color={dlsColor('grey3')} />
    : null;

  const handleRecentSearchRemove = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onRecentSearchRemove?.(option);
  }, [onRecentSearchRemove, option]);

  return (
    <div className={classes.row}>
      <button type='button'
        className={classes.rowButton}
        onClick={function() {
          onResultClick(option);
        }}>
        <span className={classes.resultIcon}>
          <Render.If condition={!!rowIcon}>
            {rowIcon}
          </Render.If>
        </span>
        <span className={classes.resultText}>
          {renderName(name, searchValue)}
        </span>
      </button>
      <Render.If condition={!searchValue}>
        <button type='button'
          className={classes.removeButton}
          aria-label={translate('common:search.removeRecentSearch')}
          onClick={handleRecentSearchRemove}>
          <MaterialExitCloseIcon size={IconSize.small} color={dlsColor('grey3')} />
        </button>
      </Render.If>
    </div>
  );
};

export default memo(SearchOverlayRow);
